import { TICK_MS } from "odyssey-shared";
import { say } from "../../words.ts";
import type { Question } from "../ask.ts";
import { oneLineDialogWidth } from "./wording.ts";

/**
 * The wording of a chosen leave that takes a decision (P2.3-T4, ADR-084's amendment of 2026-10-13): which words the
 * game menu says while a fight locks Change character and Log out, and which the page says while a character that
 * left stays in the world, by the time left. The words themselves are the language files' (`menu.leave.*` in
 * `odyssey-lang`, ADR-119).
 */

/**
 * The time the page adds to a stay before it moves on, in milliseconds: the tick in which the map server ends the
 * stay and tells the gateway, with room for the page's clock.
 */
export const STAY_MARGIN_MS = 500;

/**
 * Whole seconds left of a time in milliseconds, never under 1 while any is left.
 *
 * @param leftMs The time left.
 * @returns The seconds to show, 0 when none is left.
 */
export function secondsLeft(leftMs: number): number {
  return leftMs <= 0 ? 0 : Math.ceil(leftMs / 1000);
}

/**
 * A number of ticks as milliseconds.
 *
 * @param ticks The ticks.
 * @returns The milliseconds.
 */
export function ticksToMs(ticks: number): number {
  return ticks * TICK_MS;
}

/**
 * What the menu says under its items while a fight locks Change character and Log out.
 *
 * @param until The time on the page's clock the lock ends, or null when nothing locks them.
 * @param now The page's clock.
 * @returns The sentence, or null when there is nothing to say.
 */
export function lockNote(until: number | null, now: number): string | null {
  if (until === null) {
    return null;
  }
  const seconds = secondsLeft(until - now);
  return seconds === 0 ? say("menu.leave.lockedSoon") : say("menu.leave.locked", { seconds });
}

/**
 * The width in logical pixels of the dialog shown while a character that left stays in the world, which holds its
 * title on one line.
 */
export const STAY_DIALOG_WIDTH = 200;

/**
 * The stay dialog's sentence.
 *
 * @param seconds Whole seconds the stay has left.
 * @returns The sentence, which says the character has left once none is.
 */
export function stayNote(seconds: number): string {
  return seconds === 0 ? say("menu.leave.left") : say("menu.leave.staying", { seconds });
}

/**
 * Why Change character or Log out cannot be chosen while a fight locks it, drawn on the item.
 *
 * @param until The time on the page's clock the lock ends.
 * @param now The page's clock.
 * @returns The reason.
 */
export function lockWhy(until: number, now: number): string {
  const seconds = secondsLeft(until - now);
  return seconds === 0 ? say("menu.leave.waitSoon") : say("menu.leave.wait", { seconds });
}

/**
 * Log out's question, as the design words it, its title on one line: an action that ends the session.
 *
 * @returns The question.
 */
export function logoutQuestion(): Question {
  const title = say("menu.logoutQuestion.title");
  return { title, body: say("menu.logoutQuestion.body"), confirm: say("menu.logoutQuestion.confirm"), tone: "danger", width: Math.max(STAY_DIALOG_WIDTH, oneLineDialogWidth(title)) };
}
